import gsap from 'gsap'

/**
 * Location amenities — an animated, single-open accordion synced with the map.
 *
 * Native <details> snaps open/closed and switching items reflows the column in
 * one jump. Instead we intercept the toggle and tween the panel height + list
 * with GSAP: opening a category first collapses the current one (so heights
 * change together, not abruptly) and cross-fades in the matching pin group.
 *
 * `open` is kept set through the whole close tween (removing it would hide the
 * content instantly and kill the animation); the +/- marker tracks an `.is-open`
 * class instead. Falls back gracefully — without JS the server-rendered open
 * item and its pins are already shown.
 */
export function initAmenities() {
  const amenities = [...document.querySelectorAll('#location .amenity')]
  const pinGroups = [...document.querySelectorAll('#location .map-pins')]
  if (!amenities.length) return

  // Reduced-motion visitors get the same states, just instantly (T scales every
  // duration/delay to 0), consistent with the rest of the page.
  const T = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 1
  const PANEL_DUR = 0.5 * T
  const panelOf = (d) => d.querySelector('.amenity-content')
  const listOf = (d) => d.querySelector('ol')

  const syncPins = (key) => {
    pinGroups.forEach((group) => {
      const active = group.dataset.pins === key
      gsap.killTweensOf([group, ...group.children])
      if (active) {
        gsap.to(group, { autoAlpha: 1, duration: 0.3 * T, ease: 'power2.out' })
        // Pins pop in sequentially; opacity only, so their CSS centring transform stays intact.
        gsap.fromTo(
          group.children,
          { opacity: 0 },
          { opacity: 1, duration: 0.3 * T, ease: 'power2.out', stagger: 0.05 * T, delay: 0.08 * T }
        )
      } else {
        gsap.to(group, { autoAlpha: 0, duration: 0.25 * T, ease: 'power2.in' })
      }
    })
  }

  const expand = (d) => {
    d.open = true
    d.classList.add('is-open')
    gsap.killTweensOf([panelOf(d), listOf(d)])
    gsap.to(panelOf(d), { height: 'auto', duration: PANEL_DUR, ease: 'power3.out' })
    if (listOf(d)) {
      gsap.fromTo(
        listOf(d),
        { opacity: 0, y: 12 },
        { opacity: 1, y: 0, duration: PANEL_DUR, ease: 'power3.out', delay: 0.12 * T }
      )
    }
  }

  const collapse = (d) => {
    d.classList.remove('is-open')
    gsap.killTweensOf([panelOf(d), listOf(d)])
    if (listOf(d)) gsap.to(listOf(d), { opacity: 0, y: 12, duration: PANEL_DUR * 0.45, ease: 'power2.in' })
    gsap.to(panelOf(d), {
      height: 0,
      duration: PANEL_DUR,
      ease: 'power3.inOut',
      onComplete: () => {
        d.open = false // release the panel from the layout once fully collapsed
      },
    })
  }

  // Initial state — honour the server-rendered open item, no flash.
  let current = amenities.find((d) => d.open) || null
  amenities.forEach((d) => {
    gsap.set(panelOf(d), { height: d.open ? 'auto' : 0 })
    if (listOf(d)) gsap.set(listOf(d), { opacity: d.open ? 1 : 0, y: d.open ? 0 : 12 })
    d.classList.toggle('is-open', d.open)

    d.querySelector('summary').addEventListener('click', (event) => {
      event.preventDefault()
      if (d === current) {
        collapse(d)
        current = null
        syncPins(null)
      } else {
        if (current) collapse(current)
        expand(d)
        current = d
        syncPins(d.dataset.amenity)
      }
    })
  })

  // Cross-fade owns pin visibility now; drop the server `hidden` class and seed state.
  pinGroups.forEach((group) => {
    group.classList.remove('hidden')
    gsap.set(group, { autoAlpha: current && group.dataset.pins === current.dataset.amenity ? 1 : 0 })
  })
}
